import { forwardRef, useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import type { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, Text, Truncated, usePopoverMenu } from '@frontend/design-system';
import { LocationPhoneSelectionPopoverMenuContents } from './location-phone-selection-popover-menu-contents';
import { useBatchGetThreadStatusQuery } from './use-batch-get-thread-status-query';

export type LocationPhoneSelectorProps = {
  selectedPersonPhone: PersonPhoneSelection;
  selectedLocationPhone: LocationPhoneSelection;
  onSelectLocationPhone: (selection: LocationPhoneSelection, threadId?: string) => void;
  variant?: 'normal' | 'small';
  color?: 'white' | 'default';
  hideCurrentLocationPhone?: boolean;
  popoverState?: ReturnType<typeof usePopoverMenu>;
} & CommonHTMLAttributes;

export const LocationPhoneSelector = forwardRef<HTMLSpanElement, LocationPhoneSelectorProps>(
  (
    {
      selectedPersonPhone,
      selectedLocationPhone,
      onSelectLocationPhone,
      variant = 'normal',
      color = 'default',
      hideCurrentLocationPhone = false,
      popoverState: providedPopoverState,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('thread-header');
    const { selectedLocationIds } = useAppScopeStore();
    const locationIdsWithACL = filterLocationIdsByACLs({
      locationIds: selectedLocationIds,
      acls: Permission.SMS_INBOX_MANAGE,
    });
    const isMultiLocation = locationIdsWithACL.length > 1;
    const { availableConversationsNumbersByLocation, locationPhonesQuery } = useBatchGetThreadStatusQuery(
      selectedPersonPhone?.personPhone
    );
    const isLoading = locationPhonesQuery.isLoading;
    const selectedLocationIdAvailableNumbers = selectedLocationPhone.locationId
      ? (availableConversationsNumbersByLocation[selectedLocationPhone.locationId] || []).map(
          (number) => number.locationPhone
        )
      : [];
    const hasMultipleAvailableNumbersForSelectedLocation = selectedLocationIdAvailableNumbers.length > 1;
    const allLocationPhones = useMemo(() => locationPhonesQuery.data.allLocationPhones || [], [locationPhonesQuery]);
    const buttonLabel = useMemo(() => {
      const matchingPhone = allLocationPhones.find(
        (phone) => phone.locationPhone === selectedLocationPhone.locationPhone
      );
      if (!matchingPhone) return formatPhoneNumber(selectedLocationPhone.locationPhone, true);

      return `${matchingPhone.displayName ?? ''} ${formatPhoneNumber(matchingPhone.locationPhone, true)}`.trim();
    }, [allLocationPhones, selectedLocationPhone.locationPhone]);

    const internalPopoverState = usePopoverMenu({ placement: 'bottom-start' });
    const popoverState = providedPopoverState || internalPopoverState;
    const hasLocationPhoneOptions = allLocationPhones.length > 1;

    // TODO: remove this logic when we deprecate the `phone-numbers-name-id` FF
    const { selectedLocationIds: locationIds } = useAppScopeStore();
    const { getFeatureFlagValue } = useAppFlagStore();
    const shouldUseV4 = getFeatureFlagValue({
      locationIds,
      flagName: 'phone-numbers-name-id',
      strategy: 'ALL',
    });
    // END OF TODO

    if (!hasLocationPhoneOptions || (shouldUseV4 && isLoading)) {
      return null;
    }

    // show popover if there are multiple available numbers for the selected location
    // or if we're using the v3 experience (which always shows the popover)
    const showPopover = hasMultipleAvailableNumbersForSelectedLocation || !shouldUseV4 || isMultiLocation;

    return (
      <>
        <span
          css={{
            display: 'flex',
            alignItems: 'center',
            columnGap: shouldUseV4 ? 2 : 0,
            justifyContent: 'flex-start',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          {...rest}
          data-isopen={popoverState.isOpen}
          data-testid='inbox-thread-header-location-phone-selector'
          ref={ref}
        >
          {variant === 'normal' && (
            <Text
              color={shouldUseV4 ? 'subdued' : color === 'white' ? 'white' : 'light'}
              size={shouldUseV4 ? 'small' : 'medium'}
            >
              {t('From:')}
            </Text>
          )}
          {shouldUseV4 && <Icon name='porting-numbers-small' height={12} width={12} color='subdued' />}
          <Button
            variant={shouldUseV4 ? 'tertiary' : variant === 'small' ? 'secondary' : 'tertiary'}
            {...(!shouldUseV4 && { iconName: variant === 'small' ? 'arrow-right-small' : undefined })}
            css={[
              shouldUseV4
                ? {
                    ...{
                      padding: theme.spacing(0.375, 0.25),
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      height: 24,
                    },
                    ...(showPopover
                      ? {
                          '&:hover, &:active:not([aria-disabled="true"])': {
                            background: theme.colors.neutral5,
                          },
                        }
                      : {
                          cursor: 'default',
                          '&:hover, &:focus, &:active:not([aria-disabled="true"])': {
                            background: 'transparent',
                          },
                        }),
                  }
                : {
                    ...(variant === 'small' && {
                      border: 'none',
                      svg: {
                        rotate: '135deg',
                      },
                    }),
                    ...{ padding: theme.spacing(1, 0.75) },
                    ...(!showPopover && { cursor: 'default', pointerEvents: 'none' }),
                  },
            ]}
            {...(shouldUseV4
              ? { ...popoverState.getTriggerProps() }
              : showPopover && { ...popoverState.getTriggerProps() })}
            {...(shouldUseV4 && {
              onClick: (e) => {
                if (showPopover) {
                  popoverState.getTriggerProps().onClick?.(e);
                }
              },
            })}
            trackingId={`${InboxPrefixes.Thread}-from-location-phone-selector-button`}
          >
            {/* TODO: delete this when `phone-numbers-name-id` FF is deprecated */}
            {!shouldUseV4 && variant === 'normal' && (
              <Truncated
                as={Text}
                size='medium'
                color={showPopover ? 'primary' : 'default'}
                css={{ maxWidth: '200px', fontWeight: 'normal' }}
              >
                {buttonLabel}
              </Truncated>
            )}
            {/* END OF TODO */}
            {shouldUseV4 && (
              <Truncated
                as={Text}
                size='small'
                color='light'
                css={{
                  maxWidth: '200px',
                  fontWeight: showPopover ? 'bold' : 'normal',
                }}
              >
                {buttonLabel}
              </Truncated>
            )}
            {showPopover && (
              <Icon
                name='caret-down-tiny'
                color={color === 'white' ? 'white' : 'light'}
                style={{
                  rotate: popoverState.isOpen ? '-180deg' : '0deg',
                  transition: 'rotate 200ms ease-in-out',
                  minWidth: 'min-content',
                }}
                height={8}
                width={8}
                fontWeight={800}
              />
            )}
          </Button>
          <PopoverMenu
            {...popoverState.getMenuProps()}
            css={[{ maxWidth: 300 }, shouldUseV4 && { maxHeight: '400px !important', padding: 0 }]}
            alwaysRender
          >
            <LocationPhoneSelectionPopoverMenuContents
              activeIndex={popoverState.activeIndex ?? -1}
              getItemProps={popoverState.getItemProps}
              hideCurrentLocationPhone={hideCurrentLocationPhone}
              isOpen={popoverState.isOpen}
              onSelectLocationPhone={(locationPhone, locationId, threadId) => {
                onSelectLocationPhone({ locationPhone, locationId }, threadId);
                popoverState.close();
              }}
              selectedLocationPhone={selectedLocationPhone}
              selectedPersonPhone={selectedPersonPhone}
            />
          </PopoverMenu>
        </span>
      </>
    );
  }
);
LocationPhoneSelector.displayName = 'LocationPhoneSelector';
